<script setup lang="ts">
import type { FetchTicketResponse, NestedFileResponse } from "src/api/models";
import { getFetchFile } from "src/api/queries/order-portal-bff";
import { onMounted, ref } from "vue";
import type { FileUploadUploadEvent, FileUploadUploaderEvent } from "primevue/fileupload";
import { useFileUpload } from "src/composables/useFileUpload";
import SharedGallery from "../shared/SharedGallery.vue";
import SharedLoadingIndicator from "../shared/SharedLoadingIndicator.vue";

const props = defineProps<{
  files: NestedFileResponse[];
  ticketId: string;
}>();

const { uploadFilesToTicket } = useFileUpload();

const isLoading = ref(false);
const pictures = ref<string[]>([]);
const loading = ref(false);

function addExistingPictures() {
  const existingFileIds = props.files?.map((file) => file.id);
  if (!existingFileIds) return;

  loading.value = true;
  Promise.all(
    existingFileIds.map(async (fileId) => {
      if (!fileId) return;

      const imageUrl = await fetchFile(fileId);
      pictures.value.push(imageUrl);
    })
  ).finally(() => {
    loading.value = false;
  });
}

async function fetchFile(fileId: string) {
  const response = await getFetchFile(fileId, {
    responseType: "blob",
  });
  const blob = new Blob([response]);
  const blobUrl = URL.createObjectURL(blob);
  return blobUrl;
}

async function handleUpload(event: FileUploadUploaderEvent) {
  const files = event.files;
  try {
    if (!files) return;

    for (const file of files) {
      const reader = new FileReader();

      reader.onload = async (e) => {
        const fileIds = await uploadFilesToTicket(props.ticketId, Array.isArray(files) ? files : [files]);

        const imageDataUrl = reader.result as string;
        pictures.value.push(imageDataUrl);
      };

      reader.readAsDataURL(file);
    }
  } catch (error) {
    //
  }
}
onMounted(() => {
  addExistingPictures();
});
</script>

<template>
  <div v-if="!loading">
    <FileUpload
      mode="advanced"
      name="pictures[]"
      :multiple="true"
      accept="image/*"
      custom-upload
      @uploader="handleUpload"
      :maxFileSize="1000000"
    >
      <template #empty>
        <span>Drag and drop files to here to upload.</span>
      </template>
    </FileUpload>
    <SharedGallery :images="pictures" />
  </div>
  <div v-else>
    <SharedLoadingIndicator  />
  </div>
</template>
